import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { LogIn, Eye, EyeOff, ArrowLeft, Mail, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "CRM Login | Opulence Logistics Management"; }, []);

  // Forgot / Reset password state
  const [view, setView] = useState("login"); // "login" | "forgot" | "reset"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Check if URL has reset token
  useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) { setResetToken(t); setView("reset"); }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin-auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Login failed"); return; }
      if (data.token) localStorage.setItem("admin_token", data.token);
      onLogin(data);
    } catch {
      setError("Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setForgotError("Please enter your email."); return; }
    setForgotLoading(true);
    setForgotError(""); setForgotMsg("");
    try {
      const resetUrl = window.location.origin;
      const res = await fetch(`${API}/admin-auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail, reset_url: resetUrl })
      });
      const data = await res.json();
      if (!res.ok) { setForgotError(data.detail || "Something went wrong"); return; }
      setForgotMsg(data.message || "If that email exists, a reset link has been sent.");
    } catch {
      setForgotError("Unable to connect. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPassword || !resetConfirm) { setResetError("All fields are required."); return; }
    if (resetPassword.length < 8) { setResetError("Password must be at least 8 characters."); return; }
    if (resetPassword !== resetConfirm) { setResetError("Passwords do not match."); return; }
    setResetLoading(true);
    setResetError(""); setResetMsg("");
    try {
      const res = await fetch(`${API}/admin-auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, new_password: resetPassword })
      });
      const data = await res.json();
      if (!res.ok) { setResetError(data.detail || "Reset failed"); return; }
      setResetMsg(data.message || "Password reset! You can now log in.");
      // Clear token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => { setView("login"); setResetToken(""); }, 2000);
    } catch {
      setResetError("Unable to connect. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  const renderLogin = () => (
    <>
      <h2 className="font-cabinet text-xl font-bold text-white text-center mb-6">Sign in to CRM</h2>
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg mb-4" data-testid="login-error">{error}</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-slate-300">Email</Label>
          <Input data-testid="admin-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="you@oplogman.com" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-royal-blue-500 h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Password</Label>
          <div className="relative">
            <Input data-testid="admin-password-input" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-royal-blue-500 h-11 pr-10" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-royal-blue-600 hover:bg-royal-blue-700 h-11 text-base font-medium shadow-lg shadow-royal-blue-600/20" data-testid="admin-login-btn">
          {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><LogIn size={18} className="mr-2" /> Sign In</>}
        </Button>
      </form>
      <button onClick={() => { setView("forgot"); setForgotMsg(""); setForgotError(""); setForgotEmail(""); }}
        className="w-full text-center text-slate-400 hover:text-royal-blue-400 text-sm mt-4 transition-colors" data-testid="forgot-password-link">
        Forgot your password?
      </button>
    </>
  );

  const renderForgot = () => (
    <>
      <button onClick={() => setView("login")} className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-4 transition-colors" data-testid="back-to-login-btn">
        <ArrowLeft size={16} /> Back to sign in
      </button>
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-xl bg-royal-blue-500/20 flex items-center justify-center mx-auto mb-3">
          <Mail size={24} className="text-royal-blue-400" />
        </div>
        <h2 className="font-cabinet text-xl font-bold text-white">Reset Password</h2>
        <p className="text-slate-400 text-sm mt-1">Enter your email to receive a reset link</p>
      </div>
      {forgotMsg && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm rounded-lg mb-4" data-testid="forgot-success-msg">{forgotMsg}</div>}
      {forgotError && <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg mb-4" data-testid="forgot-error-msg">{forgotError}</div>}
      <form onSubmit={handleForgotPassword} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Email</Label>
          <Input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
            placeholder="you@oplogman.com" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-royal-blue-500 h-11" />
        </div>
        <Button type="submit" disabled={forgotLoading} className="w-full bg-royal-blue-600 hover:bg-royal-blue-700 h-11" data-testid="forgot-submit-btn">
          {forgotLoading ? "Sending..." : "Send Reset Link"}
        </Button>
      </form>
    </>
  );

  const renderReset = () => (
    <>
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
          <KeyRound size={24} className="text-amber-400" />
        </div>
        <h2 className="font-cabinet text-xl font-bold text-white">Set New Password</h2>
        <p className="text-slate-400 text-sm mt-1">Choose a strong password for your account</p>
      </div>
      {resetMsg && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm rounded-lg mb-4" data-testid="reset-success-msg">{resetMsg}</div>}
      {resetError && <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg mb-4" data-testid="reset-error-msg">{resetError}</div>}
      <form onSubmit={handleResetPassword} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">New Password</Label>
          <Input data-testid="reset-password-input" type="password" value={resetPassword} onChange={e => setResetPassword(e.target.value)}
            placeholder="Min 8 characters" className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-royal-blue-500 h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Confirm Password</Label>
          <Input data-testid="reset-confirm-input" type="password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)}
            className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-royal-blue-500 h-11" />
        </div>
        <Button type="submit" disabled={resetLoading} className="w-full bg-amber-600 hover:bg-amber-700 h-11" data-testid="reset-submit-btn">
          {resetLoading ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
      <button onClick={() => { setView("login"); setResetToken(""); window.history.replaceState({}, document.title, window.location.pathname); }}
        className="w-full text-center text-slate-400 hover:text-royal-blue-400 text-sm mt-4 transition-colors">
        Back to sign in
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-royal-blue-900 to-slate-900 flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-royal-blue-500 to-royal-blue-700 flex items-center justify-center shadow-2xl shadow-royal-blue-500/30 mx-auto mb-4">
            <span className="text-white font-bold text-2xl">O</span>
          </div>
          <h1 className="font-cabinet text-3xl font-bold text-white tracking-tight">OpLogMan</h1>
          <p className="text-slate-400 text-sm mt-1">Opulence Logistics Management Corp.</p>
        </div>
        <Card className="border-slate-700 bg-slate-800/80 backdrop-blur-sm shadow-2xl">
          <CardContent className="pt-8 pb-8 px-8">
            {view === "login" && renderLogin()}
            {view === "forgot" && renderForgot()}
            {view === "reset" && renderReset()}
          </CardContent>
        </Card>
        <p className="text-center text-slate-500 text-xs mt-6">&copy; {new Date().getFullYear()} Opulence Logistics Management Corp.</p>
      </div>
    </div>
  );
}
